import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { LoanSchedule, formatCurrency } from '@/lib/loanCalculator';
import { formatIndianCurrency } from '@/lib/formatters';

interface LoanChartsProps {
  data: LoanSchedule | null;
}

// Using SIP Returns color (accent) for Interest to maintain consistency
const COLORS = {
  principal: 'hsl(217, 91%, 50%)',
  interest: 'hsl(38, 90%, 55%)', // Same as SIP Returns (accent color)
  balance: 'hsl(217, 91%, 50%)',
  principalPaid: 'hsl(38, 90%, 55%)', // Same as SIP Returns
};

export function LoanCharts({ data }: LoanChartsProps) {
  const pieData = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Principal', value: data.schedule[0]?.openingBalance || 0 },
      { name: 'Interest', value: data.result.totalInterest },
    ];
  }, [data]);

  const areaData = useMemo(() => {
    if (!data) return [];
    // Sample every 6 months for cleaner chart
    return data.schedule
      .filter((_, index) => index % 6 === 0 || index === data.schedule.length - 1)
      .map((item) => ({
        month: item.month,
        label: `M${item.month}`,
        balance: Math.round(item.closingBalance),
        principal: Math.round(item.totalPrincipalPaid),
        interest: Math.round(item.totalInterestPaid),
      }));
  }, [data]);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64 bg-card rounded-2xl border border-border">
        <p className="text-muted-foreground">Enter loan details to see charts</p>
      </div>
    );
  }

  const principal = data.schedule[0]?.openingBalance || 0;
  const interest = data.result.totalInterest;
  const principalPercent = Math.round((principal / (principal + interest)) * 100);
  const interestPercent = 100 - principalPercent;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Pie Chart Card */}
      <div className="bg-card rounded-2xl shadow-card border border-border p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Payment Breakdown</h3>
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <div className="w-32 h-32 sm:w-40 sm:h-40 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={55}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                >
                  {pieData.map((_, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={index === 0 ? COLORS.principal : COLORS.interest} 
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-3 sm:space-y-4 w-full">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: COLORS.principal }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Principal</p>
                <p className="font-semibold text-foreground text-sm sm:text-base truncate">{formatCurrency(principal)}</p>
              </div>
              <span className="text-xs sm:text-sm font-medium text-muted-foreground shrink-0">{principalPercent}%</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: COLORS.interest }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Interest</p>
                <p className="font-semibold text-foreground text-sm sm:text-base truncate">{formatCurrency(interest)}</p>
              </div>
              <span className="text-xs sm:text-sm font-medium text-muted-foreground shrink-0">{interestPercent}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Area Chart Card */}
      <div className="bg-card rounded-2xl shadow-card border border-border p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Balance Over Time</h3>
        <div className="h-48 sm:h-64 -ml-2 sm:ml-0">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={areaData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.balance} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.balance} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="principalGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.principalPaid} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.principalPaid} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis 
                dataKey="label" 
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => `${(value / 100000).toFixed(0)}L`}
                width={35}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '12px',
                  boxShadow: 'var(--shadow-lg)',
                }}
                labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
                formatter={(value: number) => [formatIndianCurrency(value), '']}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
                formatter={(value) => <span className="text-sm text-muted-foreground">{value}</span>}
              />
              <Area
                type="monotone"
                dataKey="balance"
                name="Outstanding Balance"
                stroke={COLORS.balance}
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#balanceGradient)"
              />
              <Area
                type="monotone"
                dataKey="principal"
                name="Principal Paid"
                stroke={COLORS.principalPaid}
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#principalGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </motion.div>
  );
}
